import { useState } from 'react';
import { api, useAction } from '../lib/api';
import { money, parseAmount, toInput } from '../lib/format';
import { AccountSelect, accountLabel, useAccounts, useProducts } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { Product } from '../lib/types';
import { Icons } from '../components/icons';
import { ArchiveButton, ArchivedBadge, ShowArchived } from '../components/archive';
import { Badge, Button, Card, Empty, ErrorNote, Field, Input, Modal, MoneyInput, PageHeader, Query, Select, Table, Td, Th, Toggle, Tr } from '../components/ui';

/** How a product's revenue is recognized (core `ar/revrec.ts`), in the words a person picks from. */
export const PATTERNS: { value: Product['revenuePattern']; label: string; hint: string }[] = [
  { value: 'EVEN_PERIOD', label: 'Evenly each month', hint: 'Subscriptions and retainers: the same amount for each full month of service.' },
  { value: 'DAILY', label: 'Day by day', hint: 'Spread over the service period by the number of days, for terms that start mid-month.' },
  { value: 'POINT_IN_TIME', label: 'All at once', hint: 'One-off work such as setup or training, recognized when it is delivered.' },
];
export const patternLabel = (p: string) => PATTERNS.find((x) => x.value === p)?.label ?? p;
/** Option value the contract and invoice forms use for “+ New product…”. */
export const NEW_PRODUCT = '__new_product';

export function ProductsPage() {
  const { can, currency } = useSession();
  const products = useProducts();
  const accounts = useAccounts();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const archived = products.list.length - products.active.length;
  const add = can('ACCOUNTANT') && (
    <Button variant="primary" icon={<Icons.Plus size={15} />} onClick={() => setCreating(true)}>
      New product
    </Button>
  );
  return (
    <>
      <PageHeader
        title="Products"
        description="What you sell, and how its revenue is recognized. Contracts and invoices pick from this list."
        actions={
          <>
            <ShowArchived count={archived} value={showArchived} onChange={setShowArchived} />
            {add}
          </>
        }
      />
      <Card pad={false}>
        <Query q={products}>
          {(rows) =>
            rows.length === 0 ? (
              <Empty title="No products yet" icon={<Icons.Doc size={26} />} action={add}>
                Add each thing you sell, such as a subscription plan or an implementation package, so contracts can schedule its revenue.
              </Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Revenue recognized</Th>
                    <Th>Billing</Th>
                    <Th>Revenue account</Th>
                    <Th right>Standalone price</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows
                    .filter((p) => showArchived || !p.archivedAt)
                    .map((p) => (
                      <Tr key={p.id} onClick={can('ACCOUNTANT') ? () => setEditing(p) : undefined}>
                        <Td>
                          <div className="font-medium">
                            {p.name}
                            <ArchivedBadge at={p.archivedAt} />
                          </div>
                          {p.sku && <div className="text-[12px] text-muted">{p.sku}</div>}
                        </Td>
                        <Td>
                          <Badge tone={p.revenuePattern === 'POINT_IN_TIME' ? 'neutral' : 'info'}>{patternLabel(p.revenuePattern)}</Badge>
                        </Td>
                        <Td muted>{p.recurring ? 'Recurring' : 'One-off'}</Td>
                        <Td muted>{accountLabel(accounts.byId.get(p.revenueAccountId))}</Td>
                        <Td right className={p.ssp ? undefined : 'text-faint'}>
                          {p.ssp ? money(p.ssp, currency) : 'Not set'}
                        </Td>
                      </Tr>
                    ))}
                </tbody>
              </Table>
            )
          }
        </Query>
      </Card>
      <NewProductModal open={creating} onClose={() => setCreating(false)} />
      {editing && <EditProductModal product={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

/** Changes apply to what is billed from now on. The revenue pattern is fixed: schedules already built depend on it. */
function EditProductModal({ product: p, onClose }: { product: Product; onClose: () => void }) {
  const { currency } = useSession();
  const [f, setF] = useState({ name: p.name, sku: p.sku ?? '', recurring: p.recurring, ssp: p.ssp ? toInput(p.ssp, currency) : '', accountId: p.revenueAccountId });
  const ssp = f.ssp.trim() ? parseAmount(f.ssp, currency) : 0;
  const save = useAction(
    () => api<Product>(`/api/products/${p.id}`, { method: 'PATCH', body: { name: f.name.trim(), sku: f.sku.trim() || null, recurring: f.recurring, ssp: ssp ?? 0, revenueAccountId: f.accountId || undefined } }),
    { success: 'Product saved' },
  );
  return (
    <Modal
      open
      onClose={onClose}
      title="Edit product"
      footer={
        <>
          <span className="mr-auto">
            <ArchiveButton path={`/api/products/${p.id}`} archived={!!p.archivedAt} noun="Product" onDone={onClose} />
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!f.name.trim() || ssp === null} loading={save.isPending} onClick={() => save.mutate(undefined, { onSuccess: onClose })}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" className="col-span-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label="Revenue recognized" className="col-span-2" hint="Fixed once created. To change it, archive this product and add a new one.">
          <Input value={patternLabel(p.revenuePattern)} disabled />
        </Field>
        <Field label="SKU" hint="Optional">
          <Input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} />
        </Field>
        <Field label="Standalone price" hint="Optional" error={ssp === null ? 'Enter an amount, such as 1200' : undefined}>
          <MoneyInput currency={currency} value={f.ssp} onChange={(e) => setF({ ...f, ssp: e.target.value })} placeholder="0.00" />
        </Field>
        <Field label="Revenue account" className="col-span-2" hint="For invoices from now on">
          <AccountSelect value={f.accountId} onChange={(id) => setF({ ...f, accountId: id })} types={['INCOME']} />
        </Field>
        <div className="col-span-2">
          <Toggle checked={f.recurring} onChange={(v) => setF({ ...f, recurring: v })} label="Recurring" description="Counts toward MRR and ARR when it is on a contract." />
        </div>
      </div>
      {save.error && <ErrorNote error={save.error} className="mt-3" />}
    </Modal>
  );
}

/** Used by the Products page and, inline, by the contract and invoice forms. */
export function NewProductModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated?: (p: Product) => void }) {
  const { currency } = useSession();
  const [f, setF] = useState({ name: '', sku: '', pattern: 'EVEN_PERIOD' as Product['revenuePattern'], recurring: true, ssp: '', accountId: '' });
  const ssp = f.ssp.trim() ? parseAmount(f.ssp, currency) : 0;
  const create = useAction(
    () =>
      api<Product>('/api/products', {
        body: { name: f.name.trim(), sku: f.sku.trim() || undefined, revenuePattern: f.pattern, recurring: f.recurring, ssp: ssp ?? 0, revenueAccountId: f.accountId || undefined },
      }),
    { success: (p) => `${p.name} added` },
  );
  const close = () => {
    setF({ name: '', sku: '', pattern: 'EVEN_PERIOD', recurring: true, ssp: '', accountId: '' });
    create.reset();
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={close}
      title="New product"
      footer={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" disabled={!f.name.trim() || ssp === null} loading={create.isPending} onClick={() => create.mutate(undefined, { onSuccess: (p) => (onCreated?.(p), close()) })}>
            Add product
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" className="col-span-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Growth plan" />
        </Field>
        <Field label="Revenue recognized" className="col-span-2" hint={PATTERNS.find((p) => p.value === f.pattern)?.hint}>
          <Select value={f.pattern} onChange={(e) => setF({ ...f, pattern: e.target.value as Product['revenuePattern'], recurring: e.target.value !== 'POINT_IN_TIME' })}>
            {PATTERNS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="SKU" hint="Optional">
          <Input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} />
        </Field>
        <Field label="Standalone price" hint="Optional. Splits a bundle's price across its items" error={ssp === null ? 'Enter an amount, such as 1200' : undefined}>
          <MoneyInput currency={currency} value={f.ssp} onChange={(e) => setF({ ...f, ssp: e.target.value })} placeholder="0.00" />
        </Field>
        <Field label="Revenue account" className="col-span-2" hint="Leave empty to use the main revenue account">
          <AccountSelect value={f.accountId} onChange={(id) => setF({ ...f, accountId: id })} types={['INCOME']} placeholder="Main revenue account" />
        </Field>
        <div className="col-span-2">
          <Toggle checked={f.recurring} onChange={(v) => setF({ ...f, recurring: v })} label="Recurring" description="Counts toward MRR and ARR when it is on a contract." />
        </div>
      </div>
    </Modal>
  );
}
